import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ShieldCheck, Lock, Database, Key } from "lucide-react"; // Added Database & Key icons
import { Button } from "./ui/button";

export const SecuritySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background - Clean Dark (Removed Green Glow) */}
      <div className="absolute inset-0 bg-[#0A0F1C]" />
      <div className="absolute inset-0 noise-texture opacity-30" />
      
      <div className="container relative z-10" ref={ref}>
        <motion.div
          // Updated Box UI: Blue Borders, Subtle Shadow, Professional Look
          className="max-w-5xl mx-auto bg-[#0F1629] border border-blue-500/30 rounded-2xl p-8 md:p-12 overflow-hidden relative shadow-[0_0_40px_rgba(59,130,246,0.1)]"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row items-start gap-8">
            
            {/* Icon Badge - Changed to Blue */}
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                <ShieldCheck className="w-8 h-8 text-blue-400" />
              </div>
            </div>

            {/* Text Content */}
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-white">
                Zero-Knowledge Architecture
              </h2>
              
              <p className="text-gray-400 mb-6 text-base leading-relaxed">
                We employ a strict <strong>Single-Pass Processing</strong> protocol. Your messages are processed once to generate insights and are immediately delivered to your secure dashboard. 
              </p>
              <p className="text-gray-400 mb-8 text-base leading-relaxed">
                The raw data in our database is instantly converted into <strong>cryptographically scrambled hash</strong>. Access is mathematically tied strictly to your unique login credentials. This means ClearMakes engineers cannot view your data even if requested.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-3">
                    <Database className="w-5 h-5 text-blue-400 mt-1" />
                    <p className="text-sm text-muted-foreground">
                        <strong className="text-white block mb-0.5">Scrambled Storage</strong>
                        Data is illegible at the database level.
                    </p>
                </div>
                <div className="flex items-start gap-3">
                    <Key className="w-5 h-5 text-blue-400 mt-1" />
                    <p className="text-sm text-muted-foreground">
                        <strong className="text-white block mb-0.5">Credential-Keyed Access</strong>
                        Only your password unlocks the dashboard view.
                    </p>
                </div>
              </div>

              {/* Call to Action */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white border-none"
                  asChild
                >
                  {/* Link Recommendation below */}
                  <a href="/security-architecture">View Privacy Architecture</a>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};